import React, { useState, useEffect } from 'react';
import './index.css';

const BackupCodesModal = ({ isOpen, onClose, backupCodes }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyAllCodes = async () => {
    const allCodes = backupCodes.join('\n');
    try {
      await navigator.clipboard.writeText(allCodes);
      setCopiedIndex('all');
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy codes: ', err);
    }
  };

  const downloadBackupCodes = () => {
    // Format the backup codes as a single line with space separation
    const content = `Pronto - Backup Codes
  Generated: ${new Date().toLocaleString()}
  
  IMPORTANT: Save these backup codes in a safe place.
  You can use these codes if you lose access to your authenticator app.
  
  Backup Codes:
  ${backupCodes.join(' ')}
  
  ---
  Pronto Application
  Generated on ${new Date().toLocaleDateString()}`;
  
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pronto-backup-codes-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="backup-codes-modal-overlay" onClick={handleOverlayClick}>
      <div className="backup-codes-modal">
        <div className="backup-codes-modal-header">
          <h3>Backup Codes</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        
        <div className="backup-codes-modal-body">
          <div className="warning-message">
            <strong>Important:</strong> Save these backup codes in a safe place. 
            You can use these codes if you lose access to your authenticator app.
          </div>
          
          <div className="backup-codes-container">
            <div className="backup-codes-header">
              <h4>Your Backup Codes:</h4>
              <div className="backup-codes-actions">
                <button 
                  className="download-btn"
                  onClick={downloadBackupCodes}
                >
                Download
                </button>
                <button 
                  className="copy-all-btn"
                  onClick={copyAllCodes}
                >
                  {copiedIndex === 'all' ? '✓ Copied!' : 'Copy All'}
                </button>
              </div>
            </div>
            
            <div className="backup-codes-list">
              {backupCodes.map((code, index) => (
                <div key={index} className="backup-code-item">
                  <span className="code-number">{index + 1}.</span>
                  <span className="backup-code">{code}</span>
                  <button 
                    className="copy-single-btn"
                    onClick={() => copyToClipboard(code, index)}
                  >
                    {copiedIndex === index ? '✓' : 'Copy'}
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="backup-codes-footer">
            <p className="note">
              <strong>Note:</strong> Each backup code can only be used once. 
              Generate new codes if you run out.
            </p>
          </div>
        </div>
        
        <div className="backup-codes-modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            I've Saved These Codes
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackupCodesModal;
